import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Mail, Lock, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

export const LoginDialog = ({ open, onOpenChange, sessionExpired = false }) => {
  const { login } = useAuth();
  const { t, language } = useTranslation();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const resetFormState = () => {
    setMode('login');
    setError('');
    setInfo('');
    setLoading(false);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      resetFormState();
      setEmail('');
      setPassword('');
    }
    onOpenChange(nextOpen);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      handleOpenChange(false);
      setEmail('');
      setPassword('');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email, language });
      setInfo(response.data?.message || t('auth.forgotPasswordSent'));
      setMode('forgot-sent');
    } catch (err) {
      if (!err.response) {
        setError(t('auth.forgotPasswordNetworkError'));
      } else {
        setError(err.response?.data?.detail || t('auth.forgotPasswordFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {mode === 'login' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-gray-900">
                {sessionExpired ? t('auth.sessionExpiredTitle') : t('auth.welcomeBack')}
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {sessionExpired ? t('auth.sessionExpiredDescription') : t('auth.signInDescription')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleLoginSubmit} className="space-y-4 mt-4">
              {sessionExpired ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{t('auth.sessionExpiredHint')}</AlertDescription>
                </Alert>
              ) : null}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  {t('auth.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                    {t('auth.password')}
                  </Label>
                  <button
                    type="button"
                    onClick={() => { setError(''); setInfo(''); setMode('forgot'); }}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium whitespace-nowrap"
                  >
                    {t('auth.forgotPassword')}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('auth.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.signingIn')}
                  </>
                ) : (
                  t('auth.signIn')
                )}
              </Button>
            </form>

            <div className="text-center text-sm text-gray-600 mt-4">
              <p>{t('auth.noAccount')}</p>
            </div>
          </>
        )}

        {mode === 'forgot' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-gray-900">
                {t('auth.forgotPasswordTitle')}
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {t('auth.forgotPasswordDescription')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleForgotSubmit} className="space-y-4 mt-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-sm font-medium text-gray-700">
                  {t('auth.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.sendingResetLink')}
                  </>
                ) : (
                  t('auth.sendResetLink')
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => { setError(''); setInfo(''); setMode('login'); }}
                disabled={loading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('auth.backToSignIn')}
              </Button>
            </form>
          </>
        )}

        {mode === 'forgot-sent' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-gray-900">
                {t('auth.checkYourEmail')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <Alert className="border-green-200 bg-green-50 text-green-900">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>{info || t('auth.forgotPasswordSent')}</AlertDescription>
              </Alert>
              <p className="text-sm text-gray-600">{t('auth.forgotPasswordHint')}</p>
              <Button
                type="button"
                className="w-full bg-orange-600 hover:bg-orange-700"
                onClick={() => handleOpenChange(false)}
              >
                {t('auth.close')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
